import { isNull } from 'typeDeclarations/typeGuards';
import { DAYS_PER_MONTH, DAYS_PER_YEAR, getNumberOfDaysInBetweenDates } from 'utils/date';
import { Breakdown, ReportPerPeriodUnionNode } from './types';

/**
 * if startDate or endDate are null it means no limit exists and thus
 * the broadest breakdown is applied ('year')
 */
export function getBreakdown(startDate: Date | null, endDate: Date | null): Breakdown {
  if (isNull(startDate) || isNull(endDate)) return 'year';

  const numberOfDaysBetweenDates = getNumberOfDaysInBetweenDates({
    start: startDate,
    end: endDate,
  });

  if (numberOfDaysBetweenDates <= 2 * DAYS_PER_MONTH) return 'day';

  if (numberOfDaysBetweenDates > 2 * DAYS_PER_YEAR) return 'year';

  return 'month';
}

function getSegmentHourInMillis(segmentHour: number, ignoreOffset = false): number {
  // BT-8686: Ignore offset will create a date in UTC
  if (ignoreOffset) {
    const currentDate = new Date();
    const date = new Date(
      Date.UTC(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), segmentHour + 1, 0, 0, 0),
    );
    return date.getTime();
  } else {
    const date = new Date();
    date.setHours(segmentHour + 1, 0, 0, 0);
    return date.getTime();
  }
}

function getSegmentDateInMillis(segmentDate: string, ignoreOffset = false): number {
  // BT-9161: parsed with new Date("yyyy-MM-dd"), which is UTC midnight, so local timezones shift the calendar day.
  if (ignoreOffset) {
    // const currentDate = new Date();
    // const date = new Date(Date.UTC(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate()));
    // return date.getTime();
    const [year, month, day] = segmentDate.slice(0, 10).split('-').map(Number);
    return Date.UTC(year, month - 1, day);
  } else {
    return new Date(segmentDate).getTime();
  }
}

function getSegmentMonthInMillis(segmentMonth: number, segmentYear: number, ignoreOffset = false): number {
  // BT-9161: parsed with new Date("yyyy-MM-dd"), which is UTC midnight, so local timezones shift the calendar day.
  if (ignoreOffset) {
    return Date.UTC(segmentYear, segmentMonth - 1, 1);
  } else {
    // TODO: is this still valid??? => blame safari for not liking months with a single digit
    const date = new Date();
    date.setDate(1);
    date.setMonth(segmentMonth - 1);
    date.setFullYear(segmentYear);
    return date.getTime();
  }
}

function getSegmentYearInMillis(segmentYear: number, ignoreOffset = false): number {
  // BT-9161: parsed with new Date("yyyy-MM-dd"), which is UTC midnight, so local timezones shift the calendar day.
  if (ignoreOffset) {
    return Date.UTC(segmentYear, 0, 1);
  } else {
    return new Date(`${segmentYear}`).getTime();
  }
}

export function reportToDateInMillis(report: ReportPerPeriodUnionNode, ignoreOffset = false): number {
  if ('segmentHour' in report) {
    return getSegmentHourInMillis(report.segmentHour, ignoreOffset);
  }

  if ('segmentDate' in report) {
    return getSegmentDateInMillis(report.segmentDate, ignoreOffset);
  }

  if ('segmentMonth' in report) {
    return getSegmentMonthInMillis(report.segmentMonth, report.segmentYear, ignoreOffset);
  }

  if ('segmentYear' in report) {
    return getSegmentYearInMillis(report.segmentYear, ignoreOffset);
  }

  throw new Error(`Provided report object ${JSON.stringify(report)} has no period segment.`);
}
